import {
  CouponDiscountDuration,
  Currency,
  GetPricesResult,
  PriceValue,
  ProductAddons,
  TrialPlan,
} from 'hydra-data-types';
import { omit } from 'ts-utils';
import { PROGRAM_LENGTH } from '../../businessLogic/PlansData';
import { Analytics } from '../../util/Analytics';
import { Price, parseCurrency } from '../../util/Currency';
import { runActionSideEffect } from '../../util/StoreUtils';
import { UserFriendlyError } from '../../util/UserFriendlyError';
import { ActionType } from '../ActionTypes';
import { LOADED_STATE, LOADING_STATE } from '../LoadingAndErrorState';
import { StoreAction, StoreStateInternal } from '../Store';
import { PurchaseSelector } from '../purchase/PurchaseActions';
import { createCheckout } from '../purchase/effects/CreateCheckout';
import { PricingState } from './PricingState';
import { fetchPrices } from './effects/FetchPrices';

export type PricingAction =
  | {
      type: ActionType.SET_CURRENCY;
      currency: Currency;
    }
  | {
      type: ActionType.SET_COUPON_CODE;
      couponCode: string | undefined;
    };

export type PrivatePricingAction =
  | {
      type: ActionType.FETCH_PRICES_LOADING;
    }
  | {
      type: ActionType.FETCH_PRICES_SUCCESS;
      result: GetPricesResult;
    }
  | {
      type: ActionType.FETCH_PRICES_FAILURE;
      error: UserFriendlyError;
    };

export function pricingReducer(state: PricingState, action: StoreAction): PricingState {
  switch (action.type) {
    case ActionType.SET_CURRENCY: {
      runActionSideEffect(fetchPrices);
      runActionSideEffect(createCheckout);
      Analytics.track({
        event: Analytics.Event.o_currency_changed,
        currency: parseCurrency(action.currency),
      });
      return {
        ...state,
        currency: action.currency,
      };
    }
    case ActionType.SET_COUPON_CODE: {
      runActionSideEffect(fetchPrices);
      runActionSideEffect(createCheckout);
      return {
        ...state,
        couponCode: action.couponCode,
      };
    }
    // Entry sets the currency from the resolved country, so fetch prices and the intent in it. The root
    // reducer sets the coupon.
    case ActionType.SET_ENTRY: {
      runActionSideEffect(fetchPrices);
      runActionSideEffect(createCheckout);
      return {
        ...state,
        currency: action.entry.currency,
      };
    }
    case ActionType.FETCH_PRICES_LOADING: {
      return { ...state, itemPrices: [], ...LOADING_STATE };
    }
    case ActionType.FETCH_PRICES_SUCCESS: {
      return { ...state, ...omit(action.result, ['success']), ...LOADED_STATE };
    }
    case ActionType.FETCH_PRICES_FAILURE: {
      return { ...state, loading: false, error: action.error };
    }
    default:
      return state;
  }
}

export type Prices = {
  initialPlanPrice: Price;
  ongoingPlanPrice: Price;
  regularPlanPrice: Price;
  trialPlanPrice: Price;
  addonPlanPrice: Price | undefined;
  initialWeeklyPlanPrice: Price;
  strikethroughWeeklyPrice: Price | undefined;
  totalPriceToday: Price;
  isRecurring: boolean;
};

export const PricingSelector = {
  hasOneTimeDiscount(state: PricingState): boolean {
    return state.coupon?.duration === CouponDiscountDuration.Once;
  },

  percentRatioOff(state: PricingState): number {
    return state.coupon?.percentRatioOff ?? 0;
  },

  amountOff(state: PricingState): number {
    return state.coupon?.amountOff ?? 0;
  },

  prices(state: StoreStateInternal): Prices | undefined {
    const planPrices = state.pricing.itemPrices.find((price) => price.item === state.purchase.plan);
    const trialPlanPrices = state.pricing.itemPrices.find(
      (price) => price.item === state.purchase.trialPlan
    );
    const baselinePlan = PurchaseSelector.baselinePlan();
    const baselinePlanPrices = state.pricing.itemPrices.find(
      (price) => price.item === baselinePlan
    );
    const addonPlanPrices = state.pricing.itemPrices.find(
      (price) => price.item === state.purchase.addonPlan
    );

    if (planPrices === undefined || baselinePlanPrices === undefined) {
      return undefined;
    }
    const price = (priceValue: PriceValue) => new Price(priceValue, state.pricing.currency);

    const initialPlanPrice = planPrices.initialPrice;

    const WEEKS_PER_YEAR = 52;
    const initialWeeklyPlanPrice = price(planPrices.initialPrice / WEEKS_PER_YEAR);
    const nonDiscountedWeeklyPlanPrice = price(planPrices.regularPrice / WEEKS_PER_YEAR);
    const strikethroughWeeklyPrice =
      nonDiscountedWeeklyPlanPrice?.value === initialWeeklyPlanPrice?.value
        ? undefined
        : nonDiscountedWeeklyPlanPrice;

    const trialPlanPrice = trialPlanPrices?.initialPrice ?? 0;

    // handle addon logic: if there's an addon, add the price to the trial
    // Note: using regularPrice because coupon should not apply to addons
    const addonPlanPrice = addonPlanPrices ? addonPlanPrices.initialPrice : 0;

    const totalPriceToday = PurchaseSelector.hasTrial(state.purchase)
      ? trialPlanPrice + addonPlanPrice
      : initialPlanPrice + addonPlanPrice;

    return {
      initialPlanPrice: price(initialPlanPrice),
      ongoingPlanPrice: price(planPrices.ongoingPrice),
      regularPlanPrice: price(planPrices.regularPrice),
      initialWeeklyPlanPrice,
      strikethroughWeeklyPrice,
      trialPlanPrice: price(trialPlanPrice),
      addonPlanPrice: price(addonPlanPrice),
      totalPriceToday: price(totalPriceToday),
      isRecurring: planPrices.isRecurring,
    };
  },

  /**
   * Get a specific addon price for the current plan or a specified plan
   * @param state - The current store state
   * @param specificPlan - The plan to get the addon price for.
   * If not specified, the current chosen addon will be used
   */
  priceForAddonPlan(state: StoreStateInternal, specificPlan: ProductAddons): Price {
    // if there's a specified plan, return the prices for that plan
    // if not, read from current state to find the current selected addonPlan
    const addonPrice = state.pricing.itemPrices.find((price) =>
      specificPlan === undefined
        ? state.purchase?.addonPlan === price.item
        : specificPlan === price.item
    );

    return new Price(addonPrice?.initialPrice ?? 0, state.pricing.currency);
  },

  priceForTrialPlan(state: PricingState, trialPlan: TrialPlan): Price {
    const trialPriceValue = state.itemPrices.find(({ item }) => item === trialPlan)?.initialPrice;

    if (trialPriceValue === undefined) {
      throw new Error(`Could not find ${trialPlan} price`);
    }

    return new Price(trialPriceValue, state.currency);
  },

  perSessionPrice(state: StoreStateInternal): Price | undefined {
    const initialPrice = PricingSelector.prices(state)?.initialPlanPrice;
    return initialPrice
      ? new Price(initialPrice.value / PROGRAM_LENGTH.days, initialPrice.currency)
      : undefined;
  },
};
